import React,{useCallback} from 'react';
import {motion} from 'framer-motion';
import {useSiteContext} from '../../context/SiteContext';
import SafeIcon from '../../common/SafeIcon';
import activityTracker from '../../lib/activityTracker';

const cardVariants={
  hidden: {opacity: 0,y: 20},
  visible: {opacity: 1,y: 0,transition: {duration: 0.5,ease: "easeOut"}},
  exit: {opacity: 0,scale: 0.95,transition: {duration: 0.2}}
};

const ReferenceCard=({reference})=> {
  const {openModal,setSelectedReference}=useSiteContext();

  const handleCardClick=useCallback(()=> {
    activityTracker.trackCardClick(reference.websiteId,reference.publicTitle,reference.businessType);
    openModal(reference);
  },[reference,openModal]);

  const handleKeyDown=useCallback((e)=> {
    if (e.key==='Enter' || e.key===' ') {
      e.preventDefault();
      handleCardClick();
    }
  },[handleCardClick]);

  const handleRequestSimilar=useCallback((e)=> {
    e.stopPropagation();
    setSelectedReference(reference);
    activityTracker.trackRequestSimilar(reference.websiteId,reference.publicTitle);
    const contactSection=document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({behavior: 'smooth'});
      setTimeout(()=> {
        document.getElementById('fullName')?.focus();
      },800);
    }
  },[reference,setSelectedReference]);

  const imageSrc=reference.resolvedImageUrl || reference.imageUrl || reference.heroImageUrl || 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&auto=format&fit=crop&q=80';
  const tags=Array.isArray(reference.tags) ? reference.tags : (typeof reference.tags==='string' ? reference.tags.split(',').map(t=> t.trim()).filter(Boolean) : []);

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      whileHover={typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches ? {y: -5} : undefined}
      className={`group relative rounded-[2rem] border border-white/10 bg-gradient-to-b from-[#191923] to-[#101018] shadow-[0_24px_80px_rgba(0,0,0,0.45)] hover:shadow-[0_32px_100px_rgba(37,99,235,0.25)] hover:ring-2 hover:ring-blue-400/35 hover:border-blue-400/30 overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-500`}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${reference.publicTitle}`}
      
    >
      <div className="w-full aspect-[9/16] max-w-[360px] mx-auto rounded-[1.5rem] sm:rounded-[2rem] border-[5px] sm:border-[8px] border-white/10 bg-black overflow-hidden shadow-2xl ring-1 ring-white/10 group-hover:ring-blue-400/35 group-hover:-translate-y-2 transition-all duration-500 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-[#13131a] via-transparent to-transparent z-10 opacity-70" />
        <img src={imageSrc} alt={reference.publicTitle} className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.03] opacity-[0.86] group-hover:opacity-100" loading="lazy" decoding="async" sizes="(max-width: 479px) 90vw, (max-width: 767px) 45vw, (max-width: 1023px) 42vw, (max-width: 1439px) 30vw, 24vw" />
        <div className="absolute top-4 right-4 z-20">
          <span className="px-3 py-1.5 text-xs font-semibold bg-black/60 backdrop-blur-md border border-white/20 rounded-full text-white shadow-lg">
            {reference.businessType}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5 z-20">
          <h3 className="text-sm sm:text-lg font-bold text-white mb-1 leading-snug line-clamp-2 drop-shadow-lg">{reference.publicTitle}</h3>
          <div className="flex items-center gap-1.5 text-sm text-slate-200 drop-shadow-md">
            <SafeIcon name="MapPin" className="w-3.5 h-3.5" />
            <span className="truncate">{reference.location}</span>
          </div>
        </div>
      </div>
      <div className="p-3 sm:p-5 pt-4 sm:pt-6">
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-5">
          {tags.slice(0,2).map((tag,i)=> (
            <span key={i} className="text-[11px] px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-lg text-slate-300 font-medium">
              {tag}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-2 sm:gap-3">
          <button
            tabIndex={-1}
            
            onClick={(e)=>{e.stopPropagation();handleCardClick();}}
            className="flex items-center justify-center min-h-11 px-2 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs sm:text-sm font-semibold transition-all hover:border-white/20 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            View Page
          </button>
          <button
            onClick={handleRequestSimilar}
            tabIndex={0}
            
            className="flex items-center justify-center min-h-11 px-2 py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 border border-blue-500/30 rounded-xl text-sm font-semibold transition-all shadow-[0_4px_20px_rgba(37,99,235,0.3)] hover:shadow-[0_6px_25px_rgba(37,99,235,0.4)] text-white focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-[0_4px_20px_rgba(37,99,235,0.3)]"
          >
            Request Similar
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ReferenceCard;